import _ from 'lodash';

import { Form, FormProvider } from 'react-hook-form';

import { useLoginPassword } from './hooks';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Paper,
  Button,
  Divider,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import logo from '@assets/logo_landing_page.svg';
import LoopIcon from '@mui/icons-material/Loop';
import { StatusBanner, TextFormField } from '@components';

export const LoginPassword = () => {
  const {
    handleForgotPassword,
    onSubmit,
    isSubmitting,
    handleNextStep,
    showPassword,
    toggleShowPassword,
    httpError,
    svg,
    handleRefresh,
    loading,
    formMethods,
  } = useLoginPassword();

  const { control } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <Form
        control={control}
        onSubmit={e => onSubmit(e.event)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2.5rem',
          width: '28rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <img
            src={logo}
            style={{
              objectFit: 'cover',
              height: '131px',
              width: '132px',
            }}
            alt='logo'
          />
        </Box>

        <Paper
          elevation={8}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            paddingBlock: '3rem',
            paddingLeft: '42px',
            gap: '1rem',
            width: '100%',
            borderRadius: 0,
            overflowY: 'scroll',
            overflowX: 'hidden',
            maxHeight: '70vh',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '10px',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&:focus': {
              outline: 'none',
            },
            outline: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              padding: 0,
            }}
          >
            <AccountCircleIcon sx={{ fontSize: '3rem' }} color='primary' />
            <Typography
              sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
            >
              Sign in to continue
            </Typography>
          </Box>

          {httpError && (
            <Box
              sx={{
                pb: '0.5rem',
                width: '100%',
              }}
            >
              <StatusBanner text={httpError} variant='error' />
            </Box>
          )}

          <TextFormField
            label='Email'
            variant='outlined'
            type='email'
            name='email'
            helperText={
              'Use corporate email address e.g. @brigadehealth.com or @seniordoc.com'
            }
            required
            fullWidth
          />

          <TextFormField
            label='Password'
            variant='outlined'
            type={showPassword ? 'text' : 'password'}
            name='password'
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label={
                        showPassword
                          ? 'hide the password'
                          : 'display the password'
                      }
                      onClick={toggleShowPassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Divider sx={{ my: 0 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                dangerouslySetInnerHTML={{ __html: svg }}
                sx={{ width: 328, height: 60 }}
              />
            </Box>
            <Box
              sx={{
                width: '60px',
                height: '60px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <IconButton
                sx={{
                  width: '30px',
                  height: '30px',
                  display: 'grid',
                  padding: 0,
                  placeItems: 'center',
                  ':hover': {
                    cursor: 'pointer',
                    bgcolor: theme => theme.palette.grey[50],
                    borderRadius: '6px',
                  },
                }}
                loading={loading}
                onClick={handleRefresh}
              >
                <LoopIcon />
              </IconButton>
            </Box>
          </Box>

          <TextFormField
            label='Enter the code shown above'
            variant='outlined'
            type='text'
            name='captcha_answer'
            required
            slotProps={{ htmlInput: { maxLength: 5 } }}
          />

          <Button
            type='submit'
            variant='contained'
            color='secondary'
            sx={{
              color: 'white',
              padding: '0.5rem',
              fontWeight: '500',
              width: '100%',
            }}
            disabled={isSubmitting}
          >
            SIGN IN
          </Button>
          <Button
            variant='outlined'
            disabled={isSubmitting}
            sx={{ padding: '0.5rem', fontWeight: '500', width: '100%' }}
            onClick={handleNextStep}
          >
            USE A SIGN-IN LINK
          </Button>
          <Button
            variant='text'
            disabled={isSubmitting}
            sx={{
              padding: '0.5rem',
              fontWeight: '500',
              width: '100%',
            }}
            onClick={handleForgotPassword}
          >
            FORGOT PASSWORD?
          </Button>
        </Paper>
      </Form>
    </FormProvider>
  );
};
